import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import MatrixBackground from "../components/MatrixBackground";

const messages = [
  { text: "Initializing blog engine...", color: "text-green-400" },
  { text: "Loading post compiler...", color: "text-cyan-400" },
  { text: "Establishing encrypted connection...", color: "text-green-400" },
  { text: "Blog deployment paused...", color: "text-yellow-400" },
  { text: "System Message: See you soon.", color: "text-purple-400" },
];

const Blog = () => {
  const [displayed, setDisplayed] = useState([]);
  const [currentText, setCurrentText] = useState("");
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) return;
    const current = messages[messageIndex];
    if (!current) {
      setDone(true);
      return;
    }

    if (charIndex < current.text.length) {
      const t = setTimeout(() => {
        setCurrentText((p) => p + current.text[charIndex]);
        setCharIndex((p) => p + 1);
      }, 48);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setDisplayed((p) => [
          ...p,
          { text: current.text, color: current.color },
        ]);
        setCurrentText("");
        setCharIndex(0);
        setMessageIndex((p) => p + 1);
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [charIndex, messageIndex, done]);

  return (
    <div className="min-h-screen font-mono p-6 relative flex items-center justify-center overflow-hidden bg-[#050a0e]">
      <MatrixBackground />

      {/* Scanline overlay */}
      <div className="scanline-overlay z-[2]" />

      {/* Ambient orb */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-4 pointer-events-none"
        style={{
          background: "radial-gradient(circle, #00ff9920, transparent 70%)",
          filter: "blur(80px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-lg"
      >
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "rgba(8,18,32,0.8)",
            border: "1px solid rgba(0,255,153,0.15)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
          }}
        >
          {/* Terminal title bar */}
          <div
            className="flex items-center gap-2 px-4 py-3"
            style={{
              borderBottom: "1px solid rgba(0,255,153,0.1)",
              background: "rgba(0,0,0,0.3)",
            }}
          >
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
            <div className="w-3 h-3 rounded-full bg-green-500/70" />
            <span className="ml-3 text-xs text-green-500/40 tracking-widest">
              blog.sh
            </span>
            <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-500/60">
              <span>QUEST:</span>
              <span className="text-yellow-400/70">IN DEVELOPMENT</span>
            </div>
          </div>

          {/* Terminal body */}
          <div className="p-6 min-h-48">
            {/* Completed lines */}
            {displayed.map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 0.55 }}
                className={`text-sm mb-2 ${line.color}`}
              >
                <span className="text-green-500/40 mr-2">$</span>
                {line.text}
              </motion.div>
            ))}

            {/* Current typing line */}
            {!done && messageIndex < messages.length && (
              <div className={`text-sm mb-2 ${messages[messageIndex]?.color}`}>
                <span className="text-green-500/40 mr-2">$</span>
                {currentText}
                <span className="cursor-blink ml-0.5">▊</span>
              </div>
            )}

            {/* Final done state */}
            {done && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 text-center"
              >
                <div className="space-y-3">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs"
                    style={{
                      background: "rgba(168,85,247,0.08)",
                      border: "1px solid rgba(168,85,247,0.2)",
                    }}
                  >
                    <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                    <span className="text-purple-300 tracking-widest">
                      QUEST LOCKED
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500/60 font-mono">
                    // Unlock requirement: Complete main storyline
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Blog;
